import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Typography, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomPagination from '../../../../components/forms/Pagination';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { Item } from '../../../../app/services/api/orderManagementClient';
import NoProductImage from '../../../../assets/images/NoProductImage.png';
import RoundAndFormatValue from '../../../../components/shared/RoundAndFormatValue';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';

interface CataloguesTableProps {
  catalogueId: string | undefined;
  catalogueName: string | undefined;
  list: Item[];
  isLoading: boolean;
  searchTerm: string;
  clearAll: () => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
}

const CatalogueProductsTable = ({ catalogueId, catalogueName, list, isLoading, searchTerm, clearAll, page, setPage, totalPages }: CataloguesTableProps) => {
  const { t } = useTranslation('catalogues');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  if (!isLoading && list.length === 0) {
    return <EmptyTable
      text={t('products')}
      searchTerm={searchTerm}
      onClear={clearAll}
      onAdd={() => { }}
      addItemText={t('newProduct')}
      onAddIsCreating={false} />;
  }

  return (
    <>
      <TableContainer sx={{ borderRadius: '12px' }}
        component='div'>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
            <TableRow>
              <TableCell>
                {t('details.image')}
              </TableCell>
              <TableCell>
                {t('details.productId')}
              </TableCell>
              <TableCell>
                {t('details.internalId')}
              </TableCell>
              <TableCell>
                {t('details.name')}
              </TableCell>
              <TableCell>
                {t('details.quantityInUOM')}
              </TableCell>
              <TableCell>
                {t('details.uom')}
              </TableCell>
              <TableCell>
                {t('details.price')}
              </TableCell>
              <TableCell>
                {t('details.category')}
              </TableCell>
              <TableCell>
                {t('details.attributesStatus')}
              </TableCell>
              <TableCell>
                {t('details.documentsStatus')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row, index) => (
              <TableRow
                key={`${index}${row.name}`}
                onClick={() => navigate(`/ofd/products/${row.sku}`, { state: { catalogueId: catalogueId, catalogueName: catalogueName } })}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box
                    style={{
                      width: '100px',
                      height: isMobile ? '100px' : '75px',
                      justifyContent: 'center',
                      backgroundColor: (row?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #ddd',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'contain',
                      imageRendering: '-webkit-optimize-contrast',
                      backgroundImage: `url(${row.thumbnailUrl && row.thumbnailUrl.trim() !== '' ? row.thumbnailUrl : NoProductImage})`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'
                    fontWeight={600}
                    color={theme.palette.primary[500]}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {row.sku}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.internalId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.quantityInUom}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.orderableUom}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex"
                    flexDirection="column">
                    <Typography variant='textXS'>
                      <RoundAndFormatValue value={row.price ?? 0}
                        currencyCode={currencyCode} />
                    </Typography>
                    {row.gbpPrice !== undefined && row.price !== row.gbpPrice && (
                      <Typography
                        variant='textXS'
                        sx={{
                          color: theme.palette.text.disabled,
                          fontStyle: 'italic',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                        }}
                      >
        ({<RoundAndFormatValue value={row.gbpPrice}
                          currencyCode="GBP" />})
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.category}
                  </Typography>
                </TableCell>
                <TableCell>
                  {(row.missingRequiredAttributes && row.missingRequiredAttributes.length > 0) ? (
                    <Tooltip
                      title={`Missing attributes: ${row.missingRequiredAttributes.join(', ')}`}
                      arrow
                    >
                      <ErrorOutlineIcon
                        sx={{ color: theme.palette.warning.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="No missing attributes"
                      arrow
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: theme.palette.success.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {(row.missingRequiredDocuments && row.missingRequiredDocuments.length > 0) ? (
                    <Tooltip
                      title={`Missing documents: ${row.missingRequiredDocuments.join(', ')}`}
                      arrow
                    >
                      <ErrorOutlineIcon
                        sx={{ color: theme.palette.warning.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="No missing documents"
                      arrow
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: theme.palette.success.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {/* Add empty rows to maintain height */}
            {Array.from({ length: Math.max(0, 10 - (list.length ?? 0)) }).map((_, index) => (
              <TableRow key={`empty-row-${index}`}
                sx={{ height: '40px', backgroundColor: theme.palette.common.white }}>
                <TableCell colSpan={10}
                  sx={{
                    backgroundColor: theme.palette.common.white,
                    borderColor: index === Math.max(0, 10 - (list.length ?? 0)) - 1 ? theme.palette.custom.gray[100] : theme.palette.common.white,
                  }} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={totalPages}
        breakpointForChangeDisplay={120000}
        disabled={isLoading}
      />
    </>
  );
};

export default CatalogueProductsTable;
